import { useState } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { ordersAPI } from '../api/vendly';
import styles from './ConfirmPage.module.css';

const formatPrice = (n) => `₦${Number(n).toLocaleString()}`;

const BANK = {
  name:    'Vendly Technologies Ltd',
  bank:    'First Bank of Nigeria',
  account: '3012345678',
};

export default function ConfirmPage() {
  const { state }  = useLocation();
  const navigate   = useNavigate();
  const [copied,   setCopied]   = useState('');
  const [claimed,  setClaimed]  = useState(false);
  const [claiming, setClaiming] = useState(false);

  const copy = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopied(field);
    setTimeout(() => setCopied(''), 2000);
  };

  const handlePaid = async () => {
    setClaiming(true);
    try {
      await ordersAPI.claimPaid(state.orderId);
      setClaimed(true);
    } catch (err) {
      console.error(err);
      setClaiming(false);
    }
  };

  return (
    <div className={styles.page}>

      {/* Success popup */}
      {claimed && (
        <div className={styles.popupOverlay}>
          <div className={styles.popup}>
            <div className={styles.popupIcon}>🎉</div>
            <h2 className={styles.popupTitle}>Your order is on the way!</h2>
            <p className={styles.popupSub}>
              Admin has been notified of your payment for <strong>Order #{state.orderId}</strong>.
              Your order will be confirmed shortly.
            </p>
            <div className={styles.popupSteps}>
              <div className={styles.popupStep}><span>✅</span><p>Payment claimed</p></div>
              <div className={styles.popupStep}><span>⏳</span><p>Admin verifying</p></div>
              <div className={styles.popupStep}><span>📦</span><p>Will be shipped</p></div>
            </div>
            <Link to="/shop" className={styles.popupBtn}>Continue shopping</Link>
            <Link to="/orders" className={styles.popupLink}>View my orders</Link>
          </div>
        </div>
      )}
      <div className={`container ${styles.layout}`}>

        {/* Left */}
        <div className={styles.main}>
          <div className={styles.successHeader}>
            <div className={styles.checkCircle}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            </div>
            <div>
              <h1 className={styles.successTitle}>Order confirmed! 🎉</h1>
              <p className={styles.successSub}>Order #{state.orderId} · Transfer payment to complete your order</p>
            </div>
          </div>

          {/* Bank details */}
          {state.payMethod === 'transfer' && (
            <div className={styles.bankCard}>
              <div className={styles.bankHeader}>
                <span className={styles.bankIcon}>🏦</span>
                <div>
                  <h3 className={styles.bankTitle}>Transfer payment now</h3>
                  <p className={styles.bankSub}>Send exactly {formatPrice(state.grandTotal)} to the account below</p>
                </div>
              </div>

              <div className={styles.bankDetails}>
                <div className={styles.bankRow}>
                  <span className={styles.bankLabel}>Account name</span>
                  <div className={styles.bankValueRow}>
                    <span className={styles.bankValue}>{BANK.name}</span>
                    <button className={styles.copyBtn} onClick={() => copy(BANK.name, 'name')}>
                      {copied === 'name' ? '✓ Copied' : 'Copy'}
                    </button>
                  </div>
                </div>
                <div className={styles.bankRow}>
                  <span className={styles.bankLabel}>Bank</span>
                  <span className={styles.bankValue}>{BANK.bank}</span>
                </div>
                <div className={styles.bankRow}>
                  <span className={styles.bankLabel}>Account number</span>
                  <div className={styles.bankValueRow}>
                    <span className={`${styles.bankValue} ${styles.bankAccNum}`}>{BANK.account}</span>
                    <button className={styles.copyBtn} onClick={() => copy(BANK.account, 'account')}>
                      {copied === 'account' ? '✓ Copied' : 'Copy'}
                    </button>
                  </div>
                </div>
                <div className={styles.bankRow}>
                  <span className={styles.bankLabel}>Amount</span>
                  <div className={styles.bankValueRow}>
                    <span className={`${styles.bankValue} ${styles.bankAmount}`}>{formatPrice(state.grandTotal)}</span>
                    <button className={styles.copyBtn} onClick={() => copy(String(state.grandTotal), 'amount')}>
                      {copied === 'amount' ? '✓ Copied' : 'Copy'}
                    </button>
                  </div>
                </div>
              </div>

              {/* I HAVE PAID BUTTON — visible before scrolling */}
              {!claimed && (
                <>
                  <button
                    className={styles.paidBtn}
                    onClick={handlePaid}
                    disabled={claiming}
                  >
                    {claiming ? (
                      <span className={styles.spinner} />
                    ) : (
                      <>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                        ✅ I have sent the money
                      </>
                    )}
                  </button>
                  <p className={styles.paidNote}>
                    Only click after completing the transfer. Admin will verify and confirm.
                  </p>
                </>
              )}

              <div className={styles.bankWarning}>
                ⚠️ Use <strong>Order #{state.orderId}</strong> as your transfer narration
              </div>
            </div>
          )}

          {/* Delivery info */}
          <div className={styles.infoCard}>
            <h3 className={styles.infoTitle}>📦 Delivery details</h3>
            <div className={styles.infoGrid}>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Delivering to</span>
                <span className={styles.infoValue}>{state.address?.name}</span>
                <span className={styles.infoSub}>{state.address?.line}, {state.address?.city}, {state.address?.state}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Expected by</span>
                <span className={styles.infoValue}>{state.deliveryDate}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Contact</span>
                <span className={styles.infoValue}>{state.address?.phone}</span>
              </div>
            </div>
          </div>

          <div className={styles.actions}>
            <Link to="/orders" className={styles.ordersBtn}>View my orders</Link>
            <Link to="/home"   className={styles.shopMoreBtn}>Go to home</Link>
          </div>
        </div>

        {/* Right summary */}
        <div className={styles.sidebar}>
          <h3 className={styles.sideTitle}>Order #{state.orderId}</h3>
          {state.items?.map((item, i) => (
            <div key={i} className={styles.sideItem}>
              <img src={item.image} alt={item.name} className={styles.sideImg} />
              <div className={styles.sideItemInfo}>
                <p className={styles.sideItemName}>{item.name}</p>
                <p className={styles.sideItemMeta}>x{item.qty}</p>
              </div>
              <p className={styles.sideItemPrice}>{formatPrice(item.price * item.qty)}</p>
            </div>
          ))}
          <div className={styles.sideTotals}>
            <div className={styles.sideTotalRow}><span>Subtotal</span><span>{formatPrice(state.grandTotal - (state.items?.reduce((s,i) => s + 1000*i.qty, 0) || 0))}</span></div>
            <div className={styles.sideTotalRow}><span>Delivery</span><span>{formatPrice(state.items?.reduce((s,i) => s + 1000*i.qty, 0) || 0)}</span></div>
            <div className={`${styles.sideTotalRow} ${styles.sideGrand}`}><span>Total</span><span>{formatPrice(state.grandTotal)}</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}
